/**
 * Curriculum detail — admin page. Thin wrapper: loads the curriculum + canonical
 * modules and renders the shared `CurriculumDetailEditor`, wired to admin endpoints.
 * The editor (placement modals, canonical TopicSelector, mutations) is shared with
 * the teacher app; only the API adapter + option lists differ.
 *
 * See docs/FeatureDesigns/teacher-batch-curriculum-editing.md.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { adminApi } from '../api/adminApi';
import type { Curriculum, CurriculumPlacement } from '../types';
import { GRADE_OPTIONS, ALL_SUBJECT_OPTIONS, ALL_PROGRAMME_OPTIONS } from '@padcanvas/shared';
import { CurriculumDetailEditor, type CurriculumApiAdapter } from '@shared-ui/curriculum-view';
import './CurriculumDetail.css';

export function CurriculumDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  // The list holds its filters in the query string and hands it over on open,
  // so going back lands on the same filtered view. Absent (deep link, reload,
  // or a hard refresh) → the unfiltered list.
  const backTo = (location.state as { backTo?: string } | null)?.backTo ?? '/curriculum';

  const { data: detail } = useQuery({ queryKey: ['curriculum', id], queryFn: () => adminApi.getCurriculum(id), enabled: !!id });
  const curriculum = detail?.curriculum;
  const subject = curriculum?.subject ?? '';
  const { data: modulesData } = useQuery({ queryKey: ['modules', subject], queryFn: () => adminApi.getModules({ subject }), enabled: !!subject });

  const back = <Link to={backTo} style={{ fontSize: 12 }}>← Curricula</Link>;
  if (!curriculum) return <div className="settings-section">{back}<div style={{ padding: 24 }}>Loading…</div></div>;

  const api: CurriculumApiAdapter = {
    savePlacement: (cid, draft) => adminApi.savePlacement(cid, draft as Partial<CurriculumPlacement>),
    deletePlacement: (cid, pid) => adminApi.deletePlacement(cid, pid),
    saveCurriculum: (c) => adminApi.saveCurriculum(c as Partial<Curriculum>),
    deleteCurriculum: (cid) => adminApi.deleteCurriculum(cid),
  };
  const refetch = () => {
    qc.invalidateQueries({ queryKey: ['curriculum', id] });
    qc.invalidateQueries({ queryKey: ['curricula'] });
  };

  return (
    <CurriculumDetailEditor
      curriculum={curriculum}
      placements={detail?.placements ?? []}
      modules={modulesData?.modules ?? []}
      questionCountByPlacement={detail?.questionCountByPlacement}
      questionCountsByModule={detail?.questionCountsByModule}
      teacherQuestionCountByPlacement={detail?.teacherQuestionCountByPlacement}
      api={api}
      // The FULL programme list — this is an authoring surface, and an AP or
      // Common Core curriculum has to be nameable here to be editable at all.
      options={{ classes: [...GRADE_OPTIONS], subjects: [...ALL_SUBJECT_OPTIONS], targetExams: [...ALL_PROGRAMME_OPTIONS] }}
      onChanged={refetch}
      onDeleted={() => navigate(backTo)}
      backSlot={back}
      // A teacher's curriculum is readable here — an admin needs to see what a
      // class is being taught — but not editable: it is that teacher's work, and
      // the console offering Save would quietly rewrite it.
      readOnly={curriculum.ownerType === 'teacher'}
      headerNote={curriculum.ownerType === 'teacher'
        // A teacher's curriculum has no platform plans to author — the Study
        // Plans tab lists only non-teacher syllabuses, so a link here would
        // arrive pointing at a syllabus its own picker will not show.
        ? <span className="cv-owner-note">Teacher-owned — read only</span>
        : (
          <Link
            to={`/modules?tab=study-plans&subject=${encodeURIComponent(curriculum.subject)}&curriculumId=${encodeURIComponent(curriculum.id)}`}
            style={{ fontSize: 12 }}
          >
            Study plans for this syllabus →
          </Link>
        )}
    />
  );
}
